<script setup lang="ts">
const page = usePage().value;

const accTreks = computed(() => page?.accTreks ?? []);
const accommodation = computed(() => page?.accommodation ?? []);
const selectedCat = ref("");
const showModal = ref(false);

interface Cat {
  category: string;
}

const toggleModal = (cat: Cat) => {
  showModal.value = !showModal.value;
  selectedCat.value = cat.category;
};
const closeModal = () => {
  showModal.value = false;
};

interface Item {
  categories: { [key: string]: any };
}

// Function to sanitize strings by removing apostrophes and normalizing for comparison
const sanitizeString = (str: string): string => {
  return str
    .toLowerCase()
    .replace(/['`]/g, "") // Remove apostrophes and backticks
    .replace(/[^\w\s]/g, "") // Remove other special characters except letters, numbers, spaces
    .trim();
};

const filterAcc = computed(() => {
  return accommodation.value.filter((item: Item) => {
    let categoryString = "";

    // Check if categories is an object and extract the relevant string
    if (item.categories && typeof item.categories === "object") {
      // Assuming categories has a property 'value' which is the string we need
      if (typeof item.categories.value === "string") {
        categoryString = item.categories.value;
      } else {
        // Handle other structures within categories if necessary
        categoryString = Object.values(item.categories)
          .map((value) => (typeof value === "string" ? value : ""))
          .join(" ");
      }
    }

    return sanitizeString(categoryString).includes(sanitizeString(selectedCat.value));
  });
});
</script>

<template>
  <ClientOnly>
    <Teleport to="body">
      <Transition name="pop" appear>
        <div v-if="showModal" v-cloak class="modal">
          <div class="close-modal" @click="closeModal"></div>
          <h2>{{ selectedCat }} Accommodation</h2>
          <div class="accommodation-wrap">
            <div v-for="(acc, index) in filterAcc" :key="index" class="accommodation">
              <div class="acc-image">
                <NuxtPicture
                  v-if="acc?.accImage"
                  loading="lazy"
                  :src="acc?.accImage.url"
                  :width="acc?.accImage.width"
                  :height="acc?.accImage.height"
                  :alt="acc?.accImage.alt"
                  sizes="sm:100vw md:50vw lg:600px"
                />
              </div>
              <header>
                <h3>{{ acc.accommodation_title }}</h3>
                <h4>{{ acc.accommodation_location }}</h4>
              </header>
              <div class="acc-meta">
                <div v-html="acc.accommodation_desc" />
                <p>
                  Meal basis: <strong>{{ acc.meal_type }}</strong>
                </p>
              </div>
            </div>
          </div>
        </div>
      </Transition>
      <Transition name="fade" appear>
        <div v-if="showModal" v-cloak class="backdrop" @click="closeModal"></div>
      </Transition>
    </Teleport>
  </ClientOnly>
  <h3>Accommodation</h3>

  <section class="accommodation-wrap">
    <div
      v-for="(cat, index) in accTreks"
      :key="index"
      class="accommodation btn from-left"
      @click.prevent="toggleModal(cat)"
    >
      <div class="acc-image">
        <NuxtPicture
          v-if="cat?.catImage"
          loading="lazy"
          :src="cat?.catImage.url"
          :width="cat?.catImage.width"
          :height="cat?.catImage.height"
          :alt="cat?.catImage.alt"
          sizes="sm:100vw md:50vw lg:600px"
        />
      </div>
      <h3>{{ cat?.category }}</h3>
    </div>
  </section>
</template>
